import { BUTTER, CORAL, INK, LILAC, MINT, PAPER, PEACH, SHADOW, SKY, lerpColor } from '../../color';
import type { World as Look } from '../../world';
import {
  calmAt,
  calmCenter,
  grain,
  heartPath,
  hatchCircle,
  rgba,
  sketchCircle,
  sketchLine,
  spots,
  starPath,
  vignette,
  type Painter,
  type Spot,
} from '../../art/paint';

const TAU = Math.PI * 2;
const TINTS = [MINT, LILAC, SKY];
const TAPES = [BUTTER, MINT, LILAC, CORAL, SKY];

function page(p: Painter, look: Look): void {
  const tint = TINTS[(p.level - 1) % TINTS.length] ?? MINT;
  p.ctx.fillStyle = rgba(lerpColor(look.palette.bg, tint, 0.07));
  p.ctx.fillRect(0, 0, p.w, p.h);
  const step = Math.max(18, p.unit * 0.085);
  const ctx = p.ctx;
  ctx.lineWidth = 1;
  for (let i = 0, x = (p.cx % step) - step; x < p.w + step; i++, x += step) {
    ctx.strokeStyle = rgba(SKY, i % 5 === 0 ? 0.3 : 0.16);
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, p.h);
    ctx.stroke();
  }
  for (let j = 0, y = (p.cy % step) - step; y < p.h + step; j++, y += step) {
    ctx.strokeStyle = rgba(SKY, j % 5 === 0 ? 0.3 : 0.16);
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(p.w, y);
    ctx.stroke();
  }
  const margin = Math.min(p.w * 0.1, 96);
  ctx.strokeStyle = rgba(CORAL, 0.4);
  ctx.lineWidth = 1.4;
  for (const dx of [0, 4]) {
    ctx.beginPath();
    ctx.moveTo(margin + dx, 0);
    ctx.lineTo(margin + dx, p.h);
    ctx.stroke();
  }
}

function spiral(p: Painter): void {
  const ctx = p.ctx;
  const gap = Math.max(74, p.unit * 0.2);
  const x = Math.min(p.w * 0.035, 30);
  for (let y = gap * 0.6; y < p.h; y += gap) {
    ctx.fillStyle = rgba(INK, 0.16);
    ctx.beginPath();
    ctx.ellipse(x + 10, y, 5, 5, 0, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = rgba(lerpColor(INK, SKY, 0.35), 0.45);
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(x, y - 2, 13, 6, -0.25, Math.PI * 0.95, Math.PI * 2.05);
    ctx.stroke();
  }
}

function tape(p: Pick<Painter, 'ctx' | 'rng'>, x: number, y: number, len: number, wide: number, angle: number, color: number): void {
  const ctx = p.ctx;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  const half = len / 2;
  const hw = wide / 2;
  ctx.beginPath();
  ctx.moveTo(-half, -hw);
  ctx.lineTo(half, -hw);
  for (let i = 0; i <= 6; i++) ctx.lineTo(half + (i % 2 === 0 ? 0 : -4), -hw + (wide * i) / 6);
  ctx.lineTo(-half, hw);
  for (let i = 6; i >= 0; i--) ctx.lineTo(-half + (i % 2 === 0 ? 0 : 4), -hw + (wide * i) / 6);
  ctx.closePath();
  ctx.fillStyle = rgba(color, 0.62);
  ctx.fill();
  ctx.save();
  ctx.clip();
  const stripe = lerpColor(color, PAPER, 0.45);
  ctx.fillStyle = rgba(stripe, 0.5);
  if (p.rng.chance(0.5)) {
    for (let s = -half - wide; s < half + wide; s += 12) {
      ctx.beginPath();
      ctx.moveTo(s, -hw);
      ctx.lineTo(s + 5, -hw);
      ctx.lineTo(s + 5 + wide * 0.6, hw);
      ctx.lineTo(s + wide * 0.6, hw);
      ctx.closePath();
      ctx.fill();
    }
  } else {
    for (let s = -half + 6; s < half; s += 11) {
      for (let r = -hw + 5; r < hw; r += 9) {
        ctx.beginPath();
        ctx.arc(s + (r > 0 ? 5 : 0), r, 1.8, 0, TAU);
        ctx.fill();
      }
    }
  }
  ctx.restore();
  ctx.restore();
}

function mascotCard(p: Painter, at: Spot, look: Look): void {
  const ctx = p.ctx;
  const s = Math.max(70, p.unit * 0.24);
  const tilt = p.rng.range(-0.18, 0.18);
  ctx.save();
  ctx.translate(at.x, at.y);
  ctx.rotate(tilt);
  ctx.fillStyle = rgba(SHADOW, 0.55);
  ctx.fillRect(-s / 2 + 4, -s * 0.62 + 5, s, s * 1.24);
  ctx.fillStyle = rgba(lerpColor(PAPER, 0xffffff, 0.5), 0.92);
  ctx.fillRect(-s / 2, -s * 0.62, s, s * 1.24);
  ctx.fillStyle = rgba(PEACH, 0.55);
  ctx.beginPath();
  ctx.arc(0, -s * 0.08, s * 0.3, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(CORAL, 0.45);
  ctx.lineWidth = 1;
  hatchCircle(ctx, s * 0.06, -s * 0.02, s * 0.27, 3.2, Math.PI / 4);
  ctx.strokeStyle = rgba(INK, 0.6);
  ctx.lineWidth = 1.8;
  sketchCircle(ctx, 0, -s * 0.08, s * 0.3, p.rng, 0.03);
  ctx.fillStyle = rgba(INK, 0.7);
  ctx.beginPath();
  ctx.ellipse(-s * 0.1, -s * 0.12, s * 0.025, s * 0.04, 0, 0, TAU);
  ctx.ellipse(s * 0.1, -s * 0.12, s * 0.025, s * 0.04, 0, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, -s * 0.03, s * 0.08, 0.15 * Math.PI, 0.85 * Math.PI);
  ctx.stroke();
  ctx.fillStyle = rgba(CORAL, 0.35);
  ctx.beginPath();
  ctx.arc(-s * 0.17, -s * 0.02, s * 0.04, 0, TAU);
  ctx.arc(s * 0.17, -s * 0.02, s * 0.04, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(INK, 0.55);
  ctx.font = `${look.fontWeight} ${String(Math.round(s * 0.16))}px "${look.font}", sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('segura...', 0, s * 0.42);
  ctx.restore();
  tape(p, at.x + Math.sin(tilt) * s * 0.62, at.y - Math.cos(tilt) * s * 0.62, s * 0.55, s * 0.16, tilt + p.rng.range(-0.2, 0.2), p.rng.pick(TAPES));
}

function doodle(p: Painter, at: Spot, kind: number): void {
  const ctx = p.ctx;
  const s = p.rng.range(0.035, 0.06) * p.unit + 8;
  ctx.save();
  ctx.translate(at.x, at.y);
  ctx.rotate(p.rng.range(-0.4, 0.4));
  ctx.strokeStyle = rgba(INK, p.rng.range(0.28, 0.4));
  ctx.lineWidth = 1.6;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const marker = p.rng.pick(TAPES);
  switch (kind) {
    case 0:
      starPath(ctx, 0, 0, s, 0.45, 5, 0);
      ctx.fillStyle = rgba(marker, 0.35);
      ctx.fill();
      ctx.stroke();
      break;
    case 1: {
      ctx.fillStyle = rgba(INK, 0.35);
      ctx.beginPath();
      ctx.ellipse(-s * 0.3, s * 0.5, s * 0.22, s * 0.16, -0.4, 0, TAU);
      ctx.ellipse(s * 0.45, s * 0.35, s * 0.22, s * 0.16, -0.4, 0, TAU);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(-s * 0.1, s * 0.5);
      ctx.lineTo(-s * 0.1, -s * 0.6);
      ctx.lineTo(s * 0.65, -s * 0.75);
      ctx.lineTo(s * 0.65, s * 0.35);
      ctx.stroke();
      break;
    }
    case 2:
      heartPath(ctx, 0, 0, s * 0.8);
      ctx.fillStyle = rgba(CORAL, 0.3);
      ctx.fill();
      ctx.stroke();
      break;
    case 3: {
      ctx.beginPath();
      for (let i = 0; i < 60; i++) {
        const a = i * 0.32;
        const r = (i / 60) * s;
        if (i === 0) ctx.moveTo(0, 0);
        else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx.stroke();
      break;
    }
    case 4: {
      for (let i = 0; i < 3; i++) sketchCircle(ctx, 0, 0, s * (0.45 + i * 0.28), p.rng, 0.05);
      ctx.fillStyle = rgba(marker, 0.3);
      ctx.beginPath();
      ctx.arc(0, 0, s * 0.45, 0, TAU);
      ctx.fill();
      break;
    }
    case 5: {
      ctx.beginPath();
      ctx.moveTo(-s, s * 0.3);
      ctx.arc(-s * 0.55, s * 0.05, s * 0.4, Math.PI * 0.8, Math.PI * 1.7);
      ctx.arc(0, -s * 0.15, s * 0.5, Math.PI * 1.1, Math.PI * 1.95);
      ctx.arc(s * 0.6, s * 0.05, s * 0.4, Math.PI * 1.3, Math.PI * 2.25);
      ctx.closePath();
      ctx.fillStyle = rgba(SKY, 0.25);
      ctx.fill();
      ctx.stroke();
      break;
    }
    default: {
      sketchLine(ctx, -s, s * 0.4, s, -s * 0.2, p.rng, 0.15);
      ctx.beginPath();
      ctx.moveTo(s * 0.62, -s * 0.45);
      ctx.lineTo(s, -s * 0.2);
      ctx.lineTo(s * 0.7, s * 0.12);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function lettering(p: Painter, look: Look, at: Spot): void {
  const ctx = p.ctx;
  const size = Math.max(34, p.unit * 0.14);
  ctx.save();
  ctx.translate(at.x, at.y);
  ctx.rotate(-0.1);
  ctx.font = `${look.fontWeight} ${String(Math.round(size))}px "${look.font}", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = rgba(BUTTER, 0.55);
  ctx.fillRect(-size * 1.7, -size * 0.05, size * 3.4, size * 0.38);
  ctx.fillStyle = rgba(PEACH, 0.5);
  ctx.fillText('solta!', 3, 3);
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = rgba(INK, 0.45);
  ctx.strokeText('solta!', 0, 0);
  ctx.restore();
}

function coffee(p: Painter, at: Spot): void {
  const ctx = p.ctx;
  const r = p.unit * p.rng.range(0.12, 0.16);
  const stain = lerpColor(SHADOW, CORAL, 0.35);
  ctx.strokeStyle = rgba(stain, 0.28);
  ctx.lineWidth = 3;
  const start = p.rng.float() * TAU;
  ctx.beginPath();
  ctx.arc(at.x, at.y, r, start, start + TAU * 0.8);
  ctx.stroke();
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(at.x + 3, at.y - 2, r * 0.94, start + 1, start + 1 + TAU * 0.6);
  ctx.stroke();
  ctx.fillStyle = rgba(stain, 0.07);
  ctx.beginPath();
  ctx.arc(at.x, at.y, r, 0, TAU);
  ctx.fill();
}

function smudges(p: Painter): void {
  const ctx = p.ctx;
  for (let i = 0; i < 5; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * p.h;
    const r = p.unit * p.rng.range(0.08, 0.2);
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, rgba(INK, 0.05));
    g.addColorStop(1, rgba(INK, 0));
    ctx.fillStyle = g;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(1.8, 0.7);
    ctx.translate(-x, -y);
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
    ctx.restore();
  }
}

export function paint(p: Painter, look: Look): void {
  page(p, look);
  smudges(p);
  const keep = 1.05;
  const corners: Spot[] = [
    { x: p.w * 0.12, y: p.h * 0.1 },
    { x: p.w * 0.9, y: p.h * 0.12 },
    { x: p.w * 0.1, y: p.h * 0.9 },
    { x: p.w * 0.9, y: p.h * 0.88 },
  ];
  for (const c of corners) {
    if (p.rng.chance(0.75)) tape(p, c.x, c.y, p.unit * 0.3 + 30, p.unit * 0.07 + 10, (c.x < p.cx ? -1 : 1) * (c.y < p.cy ? -0.7 : 0.7), p.rng.pick(TAPES));
  }
  const places = spots(p, 16, keep, 28, Math.max(60, p.unit * 0.22));
  const card = places.shift();
  if (card !== undefined && !calmAt(p, card.x, card.y, keep + 0.15)) mascotCard(p, card, look);
  const word = places.shift();
  if (word !== undefined) lettering(p, look, word);
  const cup = places.shift();
  if (cup !== undefined) coffee(p, cup);
  places.forEach((at, i) => {
    doodle(p, at, (i + p.level) % 7);
  });
  spiral(p);
  calmCenter(p, look.palette.bg, 0.7, 1.2);
  grain(p, 0.1, false);
  vignette(p, SHADOW, 0.55);
}
